import {
  Account_Read_Driver_Nested,
  CompanyShare_Create,
  CompanyShare_Read,
  CompanyShare_Read_Driver_Nested,
  CompanyShare_Read_Nested,
  CompanyShare_Update,
  CompanyShareable_Read_Nested,
  CompanyShareableType,
} from '@treadinc/horizon-api-spec';

import { CompanyBasic } from '~hooks/useCompany/models';
import { BasicEquipmentDefault } from '~hooks/useEquipment/models';
import { Nullable } from '~types/Nullable';

export class CompanyShareableBasic {
  constructor(
    private _id: string,
    private _type: CompanyShareableType,
    private _name: string,
  ) {}

  public get id(): string {
    return this._id;
  }

  public get type(): CompanyShareableType {
    return this._type;
  }

  public get name(): string {
    return this._name;
  }

  public static parse(proto: CompanyShareable_Read_Nested) {
    return new CompanyShareableBasic(proto.id, proto.type, proto.name);
  }
}
export class CompanyShareBasic {
  constructor(
    private _id: string,
    private _external_id: Nullable<string>,
    private _sender_company: CompanyBasic,
    private _dispatchable: boolean,
    private _ticket_match_id: Nullable<string>,
    private _schedule_on: boolean,
    private _rate_override: boolean,
  ) {}

  public get id(): string {
    return this._id;
  }

  public get externalId(): Nullable<string> {
    return this._external_id;
  }

  public get senderCompany(): CompanyBasic {
    return this._sender_company;
  }

  public get dispatchable(): boolean {
    return this._dispatchable;
  }

  public get ticketMatchId(): Nullable<string> {
    return this._ticket_match_id;
  }

  public get scheduleOn() {
    return this._schedule_on;
  }

  public get rateOverride(): boolean {
    return this._rate_override;
  }

  public static parse(proto: CompanyShare_Read_Nested) {
    return new CompanyShareBasic(
      proto.id,
      proto.external_id,
      CompanyBasic.parse(proto.sender_company),
      proto.dispatchable,
      proto.ticket_match_id,
      proto.schedule_on,
      proto.rate_override,
    );
  }
}

export class CompanyShare extends CompanyShareBasic {
  constructor(
    _id: string,
    _external_id: Nullable<string>,
    _sender_company: CompanyBasic,
    _dispatchable: boolean,
    _ticket_match_id: Nullable<string>,
    private _parent_company_share_id: Nullable<string>,
    private _receiver_company: CompanyBasic,
    private _company_shareable: CompanyShareableBasic,
    _schedule_on: boolean,
    _rate_override: boolean,
  ) {
    super(
      _id,
      _external_id,
      _sender_company,
      _dispatchable,
      _ticket_match_id,
      _schedule_on,
      _rate_override,
    );
  }

  public get parentCompanyShareeId(): Nullable<string> {
    return this._parent_company_share_id;
  }

  public get receiverCompany(): CompanyBasic {
    return this._receiver_company;
  }

  public get companyShareable(): CompanyShareableBasic {
    return this._company_shareable;
  }

  public static parse(proto: CompanyShare_Read) {
    return new CompanyShare(
      proto.id,
      proto.external_id,
      CompanyBasic.parse(proto.sender_company),
      proto.dispatchable,
      proto.ticket_match_id,
      proto.parent_company_share_id,
      CompanyBasic.parse(proto.receiver_company),
      CompanyShareableBasic.parse(proto.company_shareable),
      proto.schedule_on,
      proto.rate_override,
    );
  }

  public static deparse(proto: any): CompanyShare_Create {
    const data = {
      receiver_company_id: proto.receiver_company_id,
      company_shareable_id: proto.company_shareable_id,
      company_shareable_type: proto.company_shareable_type,
      dispatchable: proto.dispatchable,
      ticket_match_id: proto.ticket_match_id,
      rate_override: proto.rate_override,
    };

    return data as CompanyShare_Create;
  }

  public static deparseUpdate(proto: any): CompanyShare_Update {
    const data = {
      external_id: proto.external_id,
      ticket_match_id: proto.ticket_match_id,
      dispatchable: proto.dispatchable,
      schedule_on: proto.schedule_on,
      rate_override: proto.rate_override,
      default_equipment_id: proto.default_equipment_id,
      default_additional_equipment_ids: proto.default_additional_equipment_ids,
    };

    return data as CompanyShare_Update;
  }
}

export class CompanyShareUserBasic extends CompanyShareBasic {
  constructor(
    _id: string,
    _external_id: Nullable<string>,
    _sender_company: CompanyBasic,
    _dispatchable: boolean,
    _ticket_match_id: Nullable<string>,
    _schedule_on: boolean,
    _rate_override: boolean,
    private _default_equipment: Nullable<BasicEquipmentDefault>,
    private _default_additional_equipment: BasicEquipmentDefault[],
    private _expires_at: Nullable<string>,
    private _receiver_account: Nullable<AccountDriverBasic>,
  ) {
    super(
      _id,
      _external_id,
      _sender_company,
      _dispatchable,
      _ticket_match_id,
      _schedule_on,
      _rate_override,
    );
  }

  public get defaultEquipment(): Nullable<BasicEquipmentDefault> {
    return this._default_equipment;
  }

  public set defaultEquipment(equipment: Nullable<BasicEquipmentDefault>) {
    this._default_equipment = equipment;
  }

  public get defaultAdditionalEquipment() {
    return this._default_additional_equipment;
  }

  public set defaultAdditionalEquipment(value: BasicEquipmentDefault[]) {
    this._default_additional_equipment = value;
  }

  public get expiresAt(): Nullable<string> {
    return this._expires_at;
  }

  public get receiverAccount() {
    return this._receiver_account;
  }

  public static parse(proto: CompanyShare_Read_Driver_Nested) {
    return new CompanyShareUserBasic(
      proto.id,
      proto.external_id,
      CompanyBasic.parse(proto.sender_company),
      proto.dispatchable,
      proto.ticket_match_id,
      proto.schedule_on,
      proto.rate_override,
      proto.default_equipment
        ? BasicEquipmentDefault.parse(proto.default_equipment)
        : null,
      (proto.default_additional_equipment ?? []).map((equipment) =>
        BasicEquipmentDefault.parse(equipment),
      ),
      proto.expires_at,
      proto.receiver_account ? AccountDriverBasic.parse(proto.receiver_account) : null,
    );
  }
}
export class AccountDriverBasic {
  public static parse(proto: Account_Read_Driver_Nested) {
    return new AccountDriverBasic(
      proto.id,
      proto.name,
      proto.external_id,
      CompanyBasic.parse(proto.company),
    );
  }

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get externalId() {
    return this._external_id;
  }

  public get company() {
    return this._company;
  }

  constructor(
    private _id: string,
    private _name: string,
    private _external_id: Nullable<string>,
    private _company: CompanyBasic,
  ) {}
}
